import { computed, makeObservable, observable, action } from "mobx";
import { DbRecording, UUID } from "./DatabaseTypes";
import { requireEvidenceDatabase } from "./getDatabase";
import { friendlyTimeSince } from "../../lib/utils/friendlyTimeSince";
import type { EvidenceDatabase } from "./EvidenceDbStruct";
import { RecordingDeleted, RecordingUpdated } from "../StateEvents";


export class DbRecordingEntryReader {
	_recording: DbRecording | undefined;
	populatedName: string | undefined;

	@computed get recording() {
		return this._recording == null ? undefined : { ...this._recording };
	}

	static fetchFromDb = (db: EvidenceDatabase, recordingUuid: UUID) =>
		db.get("recordings", recordingUuid);

	static fetch = async ( recordingUuid: UUID) =>
		await (await requireEvidenceDatabase()).get("recordings", recordingUuid);

	setRecordingForThisObjectOnly = action ( (recording: DbRecording | undefined) => {
		this._recording = recording == null ? undefined : {...recording};
	})

	updateFromDatabase = async () => {
		// ProcessInfo.log(`Updating local copy of recording ${this.recordingUuid}`);
		const updatedRecording = await DbRecordingEntryReader.fetchFromDb(this.db, this.recordingUuid);
		if (updatedRecording != null) {
			this.setRecordingForThisObjectOnly(updatedRecording);
		}
	};

	#loadFromDatabaseCallOnlyFromConstructor = async () => {
		let backoffAwaitMs = 0;
		do {
			const recording = await DbRecordingEntryReader.fetchFromDb(this.db, this.recordingUuid);
			if (recording != null) {
				this.setRecordingForThisObjectOnly(recording);
				return;
			}
			if (backoffAwaitMs < 1000) {
				backoffAwaitMs += 100;
			}
			await new Promise( resolve => setTimeout(resolve, backoffAwaitMs));
		// eslint-disable-next-line no-constant-condition
		} while (true);
	}

	@computed get mostCommonPageRegisteredDomainInNetworkRequests() {
		const [mostCommonPageRegisteredDomainInNetworkRequests] = [...this.recording?.dataSummary?.sourcePageRegisteredDomains?.values() ??[]]
			.sort( (a, b) => b.numberOfRequests - a.numberOfRequests );
		if (mostCommonPageRegisteredDomainInNetworkRequests != null) {
			return mostCommonPageRegisteredDomainInNetworkRequests.registeredDomain;
		}
	}

	@computed get placeholderName() {
		const recording = this.recording;
		if (recording?.name != null && recording.name.length > 0) {
			return recording.name;
		} else if ( recording != null && this.mostCommonPageRegisteredDomainInNetworkRequests != null) {
			return `${this.mostCommonPageRegisteredDomainInNetworkRequests} at ${new Date(recording.startTimeIso).toLocaleString()}`;
		}
		if (recording == null) {
			return "Recording";
		}
		return `Recording started ${(new Date(recording.startTimeIso).toLocaleString())}`;
	}


	@computed get name() {
		return this.recording?.name ?? undefined;
	}

	@computed get nameOrDescription() {
		if (this.name != null && this.name.length > 0) return this.name;
		if (this.inProgress) return "Recording in progress";
		const recording = this.recording;
		const dataSummary = recording?.dataSummary;
		if (dataSummary != null) return `Recording ended ${ friendlyTimeSince(dataSummary.whenLastObservedIso) }`;
		if (recording?.startTimeIso == null) return "Recording";
		return `Recording started ${ friendlyTimeSince(recording.startTimeIso) }`;
	}


	@computed get status() {
		return this.recording?.status;
	}

	@computed get inProgress() {
		return this.status === "recording";
	}


	@computed get startTime() { return this.recording == null ? undefined : new Date(this.recording.startTimeIso); }
	@computed get startTimeMs() { return this.startTime?.getTime(); }

	getName = () => {
		if (this.name != null && this.name.length > 0) {
			return this.name;
		} else  {
			return this.placeholderName;
		}
	}



	constructor(
		readonly db: EvidenceDatabase,
		readonly recordingUuid: UUID,
		initialValue?: DbRecording,
	) {
		this._recording = initialValue;
		makeObservable(this, {_recording: observable});
		DbRecordingEntryReader.#forRecordingUuid.set(recordingUuid, this);
		if (initialValue == null) {
			this.#loadFromDatabaseCallOnlyFromConstructor();
		}
		RecordingUpdated.addListener( async ({recordingUuid}) => {
			if (this.recordingUuid !== recordingUuid) return;
			const updatedValued = await DbRecordingEntryReader.fetch(recordingUuid);
			if (updatedValued != null) {
				this.setRecordingForThisObjectOnly(updatedValued);
			}
		});
		RecordingDeleted.addListener( ({recordingUuid}) => {
			if (this.recordingUuid === recordingUuid) {
				this.setRecordingForThisObjectOnly(undefined);
			}
		});
	}

	static #forRecordingUuid = new Map<UUID, DbRecordingEntryReader>();

	static fromRecordingUuid = (recordingUuid: UUID) => {
		return DbRecordingEntryReader.#forRecordingUuid.get(recordingUuid);
	}

	static fromDbAndRecording = (db: EvidenceDatabase, recording: DbRecording) => {
		let reader = DbRecordingEntryReader.#forRecordingUuid.get(recording.recordingUuid);
		if (reader == null) {
			reader = new DbRecordingEntryReader(db, recording.recordingUuid, recording);
		} else if (reader.recording == null) {
			reader.setRecordingForThisObjectOnly(recording);
		}
		return reader;
	}

	static fromRecordingUuidDeferredLoad = (db: EvidenceDatabase, recordingUuid: UUID) => {
		return DbRecordingEntryReader.#forRecordingUuid.get(recordingUuid) ?? new DbRecordingEntryReader(db, recordingUuid, undefined);
	}

	static fromRecordingUuidAwaitLoad = async (db: EvidenceDatabase, recordingUuid: UUID) => {
		return DbRecordingEntryReader.#forRecordingUuid.get(recordingUuid) ?? new DbRecordingEntryReader(db, recordingUuid, await DbRecordingEntryReader.fetchFromDb(db, recordingUuid));
	}

}


